import React, { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { useLang } from "../../context/LangContext";
import { useScrolled } from "../../hooks/useScrolled";
import { useActiveSection } from "../../hooks/useActiveSection";
import { ROUTES } from "../../_internal";
import LangSwitcher from "../ui/LangSwitcher";
import Button from "../ui/Button";
import LogoFull from "../../assets/logos/LogoFull";

const SECTION_IDS = [
  "acasa",
  "servicii",
  "despre",
  "avantaje",
  "parteneri",
  "contact",
  "locatii",
];

interface NavItem {
  label: string;
  id: string;
  href: string;
  isPageLink?: boolean;
}

interface NavLinkProps {
  item: NavItem;
  isActive: boolean;
  onClick?: () => void;
  mobile?: boolean;
}

function NavLink({ item, isActive, onClick, mobile = false }: NavLinkProps) {
  const commonProps = {
    onClick,
    "aria-current": isActive ? ("page" as const) : undefined,
  };

  if (mobile) {
    const El = item.isPageLink ? Link : "a";
    const linkProp = item.isPageLink ? { to: item.href } : { href: item.href };
    return (
      <El
        {...(linkProp as any)}
        {...commonProps}
        className={`block px-8 py-3 text-base transition-colors
          ${isActive ? "text-teal font-semibold" : "text-silver hover:text-teal"}`}
      >
        {item.label}
      </El>
    );
  }

  if (item.isPageLink) {
    return (
      <Link
        to={item.href}
        onClick={onClick}
        className={`relative text-sm font-medium px-3 py-1.5 rounded-full transition-all duration-200 tracking-wide
          ${isActive ? "text-white bg-white/[0.1]" : "text-silver hover:text-white hover:bg-white/[0.08]"}`}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <a
      href={item.href}
      onClick={onClick}
      className={`relative text-sm font-medium px-3 py-1.5 rounded-full transition-all duration-200 tracking-wide
        ${isActive ? "text-white bg-white/[0.1]" : "text-silver hover:text-white hover:bg-white/[0.08]"}`}
    >
      {item.label}
      {isActive && (
        <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-teal" />
      )}
    </a>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const scrolled = useScrolled(50);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === ROUTES.HOME;
  const activeSection = useActiveSection(isHome ? SECTION_IDS : []);

  const navItems: NavItem[] = [
    { label: t.nav.home, id: "acasa", href: isHome ? "#acasa" : "/#acasa" },
    {
      label: t.nav.services,
      id: "servicii",
      href: isHome ? "#servicii" : "/#servicii",
    },
    { label: t.nav.about, id: "despre", href: isHome ? "#despre" : "/#despre" },
    {
      label: t.nav.advantages,
      id: "avantaje",
      href: isHome ? "#avantaje" : "/#avantaje",
    },
    {
      label: t.nav.partners,
      id: "parteneri",
      href: isHome ? "#parteneri" : "/#parteneri",
    },
    {
      label: t.nav.contact,
      id: "contact",
      href: isHome ? "#contact" : "/#contact",
    },
    {
      label: t.nav.locations,
      id: "locatii",
      href: isHome ? "#locatii" : "/#locatii",
    },
    {
      label: t.nav.catalog,
      id: "catalog",
      href: ROUTES.CATALOG,
      isPageLink: true,
    },
  ];

  const navBg =
    scrolled || !isHome ? "bg-navy backdrop-blur-md shadow-lg py-3" : "py-5";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 ${navBg}`}
    >
      <div className="container flex items-center gap-8">
        <Link
          to={ROUTES.HOME}
          aria-label="EccoFarm home"
          className="flex-shrink-0"
        >
          <LogoFull variant="light" height={38} />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              item={item}
              isActive={isHome && activeSection === item.id}
            />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitcher />
          <Button
            as="a"
            href={`tel:${t.company.phone}`}
            variant="primary"
            size="sm"
            iconLeft={<Phone size={14} />}
          >
            {t.company.phone}
          </Button>
        </div>

        <button
          className="lg:hidden ml-auto text-white p-1"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden bg-navy overflow-hidden transition-all duration-300 ${open ? "max-h-[500px] pb-6" : "max-h-0"}`}
      >
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            item={item}
            isActive={isHome && activeSection === item.id}
            onClick={() => setOpen(false)}
            mobile
          />
        ))}
        <div className="flex items-center justify-between px-8 pt-3 mt-2 border-t border-white/[0.06]">
          <a
            href={`tel:${t.company.phone}`}
            className="flex items-center gap-2 text-teal font-semibold text-sm"
          >
            <Phone size={15} /> {t.company.phone}
          </a>
          <LangSwitcher />
        </div>
      </div>
    </header>
  );
}
